import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  List,
  ListItem,
  Typography,
} from "@mui/material";
import { useState, type FunctionComponent, useCallback, useMemo } from "react";
import type FeatureNodeType from "../features/FeatureNodeType";
import type FeatureOperation from "../features/FeatureOperation";
import DraggableItem from "./DraggableItem";
import CollapsiblePanel from "./CollapsiblePanel";
import EditOperationDialog from "./EditOperationDialog";

interface AddNodesPanelProps {
  featureOperations: FeatureOperation[];
  operationIdsAddedAtLeastOnce: ReadonlySet<string>;
  isDarkMode: boolean;
  onAddNode: (featureNodeType: FeatureNodeType) => void;
  onAddOperation: () => void;
  onEditOperation: (updatedOperation: FeatureOperation) => void;
  onDeleteOperation: (operationId: string) => void;
}

const AddNodesPanel: FunctionComponent<AddNodesPanelProps> = ({
  featureOperations,
  operationIdsAddedAtLeastOnce,
  isDarkMode,
  onAddNode,
  onAddOperation,
  onEditOperation,
  onDeleteOperation,
}) => {
  const builtInOperationTypes: string[] = useMemo(
    () => ["basic", "aggregate", "trigonometric", "activation", "loss"],
    [],
  );

  const customOperations = useMemo(
    () => featureOperations.filter((operation) => operation.type === "custom"),
    [featureOperations],
  );

  const [isEditDialogOpen, setEditDialogOpen] = useState(false);
  const [editingOperation, setEditingOperation] =
    useState<FeatureOperation | null>(null);

  const capitalizeFirstLetter = useCallback((word: string): string => {
    return word[0].toUpperCase() + word.slice(1);
  }, []);

  const handleOpenEditDialog = useCallback(
    (featureOperation: FeatureOperation) => {
      setEditingOperation(featureOperation);
      setEditDialogOpen(true);
    },
    [],
  );

  const handleCloseEditDialog = useCallback(() => {
    setEditDialogOpen(false);
  }, []);

  const handleSaveOperation = useCallback(
    (updatedOperation: FeatureOperation) => {
      setEditDialogOpen(false);
      onEditOperation(updatedOperation);
    },
    [onEditOperation],
  );

  const handleDeleteOperation = useCallback(
    (operationId: string) => {
      setEditDialogOpen(false);
      onDeleteOperation(operationId);
    },
    [onDeleteOperation],
  );

  return (
    <>
      <CollapsiblePanel title="Add Nodes">
        {/* Value nodes */}
        <Accordion defaultExpanded disableGutters square sx={{ width: "100%" }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="value-content"
            id="value-header"
          >
            <Typography variant="subtitle2">Value</Typography>
          </AccordionSummary>
          <AccordionDetails id="value-content" sx={{ p: 0 }}>
            <List>
              <DraggableItem
                featureNodeType={{ nodeType: "constant" }}
                text="Constant"
                helpText="Set a constant value"
                editable={false}
                onClickItem={onAddNode}
                onClickEditIcon={null}
              />
              <DraggableItem
                featureNodeType={{ nodeType: "variable" }}
                text="Variable"
                helpText="Set a variable"
                editable={false}
                onClickItem={onAddNode}
                onClickEditIcon={null}
              />
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Built-in operations */}
        {builtInOperationTypes.map((builtInOperationType) => {
          const filteredOperations = featureOperations.filter(
            (operation) => operation.type === builtInOperationType,
          );

          return (
            <Accordion
              key={builtInOperationType}
              defaultExpanded
              disableGutters
              square
              sx={{ width: "100%" }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`${builtInOperationType}-content`}
                id={`${builtInOperationType}-header`}
              >
                <Typography variant="subtitle2">
                  {capitalizeFirstLetter(builtInOperationType)}
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                id={`${builtInOperationType}-content`}
                sx={{ p: 0 }}
              >
                <List>
                  {filteredOperations.map((operation) => (
                    <DraggableItem
                      key={operation.id}
                      featureNodeType={{
                        nodeType: "operation",
                        operationId: operation.id,
                      }}
                      text={operation.name}
                      helpText={operation.helpText}
                      editable
                      onClickItem={onAddNode}
                      onClickEditIcon={() => {
                        handleOpenEditDialog(operation);
                      }}
                    />
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          );
        })}

        {/* Custom operations */}
        <Accordion defaultExpanded disableGutters square sx={{ width: "100%" }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="custom-content"
            id="custom-header"
          >
            <Typography variant="subtitle2">Custom</Typography>
          </AccordionSummary>
          <AccordionDetails id="custom-content" sx={{ p: 0 }}>
            <List>
              {customOperations.map((operation) => (
                <DraggableItem
                  key={operation.id}
                  featureNodeType={{
                    nodeType: "operation",
                    operationId: operation.id,
                  }}
                  text={operation.name}
                  helpText={operation.helpText}
                  editable
                  onClickItem={onAddNode}
                  onClickEditIcon={() => {
                    handleOpenEditDialog(operation);
                  }}
                />
              ))}
              {/* Add operation */}
              <ListItem
                onClick={() => {
                  onAddOperation();
                }}
              >
                <Button startIcon={<AddIcon />}>Add Operation</Button>
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>
      </CollapsiblePanel>

      {/* Operation editing dialog */}
      {editingOperation !== null && isEditDialogOpen && (
        <EditOperationDialog
          open={isEditDialogOpen}
          readOperation={editingOperation}
          operationIdsAddedAtLeastOnce={operationIdsAddedAtLeastOnce}
          isDarkMode={isDarkMode}
          onCancel={handleCloseEditDialog}
          onSave={handleSaveOperation}
          onDelete={handleDeleteOperation}
        />
      )}
    </>
  );
};

export default AddNodesPanel;
